/* Keep the native Shopify form and CAPTCHA. Enhance only token-ready submissions. */
function mountNewsletter(form, {signal, on, find}) {
  if (!form) return;
  const button = form.querySelector('[type="submit"]');
  const label = button?.querySelector('.button-label') || button;
  const originalLabel = label?.textContent;
  const status = form.querySelector('[data-newsletter-status]');
  let pending = false;
  const feedback = (message, state) => {
    form.dataset.newsletterState = state;
    if (status) {
      status.textContent = message;
      status.setAttribute('role', state === 'error' ? 'alert' : 'status');
    }
  };
  const busy = value => {
    pending = value;
    form.setAttribute('aria-busy', String(value));
    button?.setAttribute('aria-disabled', String(value));
    if (label) label.textContent = value ? form.dataset.pendingLabel : originalLabel;
  };
  on(window, 'pageshow', () => busy(false));
  on(form, 'input', () => {
    if (!pending && form.dataset.newsletterState === 'pending') {
      busy(false);
      feedback('', 'idle');
    }
  });
  on(form, 'submit', async event => {
    if (event.defaultPrevented) return;
    if (pending) { event.preventDefault(); return; }
    const body = new FormData(form);
    const token = body.get('h-captcha-response');
    const action = new URL(form.action, document.baseURI);
    // Without a ready CAPTCHA token Shopify retains its normal submit/challenge flow.
    const asynchronous = typeof token === 'string' && token.trim() &&
      action.origin === location.origin && /\/contact$/.test(action.pathname);
    busy(true);
    feedback(form.dataset.pendingLabel, 'pending');
    if (!asynchronous) {
      // CAPTCHA can be canceled without navigating. Keep the native button usable.
      pending = false;
      button?.setAttribute('aria-disabled', 'false');
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const request = new AbortController();
    const abort = () => request.abort();
    signal.addEventListener('abort', abort, {once: true});
    const timeout = setTimeout(abort, 30000);
    try {
      action.hash = '';
      const response = await fetch(action.href, {
        method: 'POST', body, credentials: 'same-origin', signal: request.signal
      });
      if (signal.aborted) return;
      const destination = new URL(response.url || action.href);
      if (destination.origin === location.origin && /\/challenge(?:\/|$)/.test(destination.pathname)) {
        location.assign(destination.href);
        return;
      }
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = page.getElementById(form.id);
      if (!response.ok || !result?.querySelector('[data-newsletter-success]')) {
        const error = result?.querySelector('[data-newsletter-error]')?.textContent?.trim();
        throw new Error(error || form.dataset.errorMessage);
      }
      if (signal.aborted) return;
      feedback(form.dataset.successMessage, 'success');
      form.reset();
      const dialog = find('.newsletter-success');
      if (dialog && !dialog.open) dialog.showModal();
    } catch (error) {
      if (!signal.aborted) feedback(error.name === 'AbortError' ? form.dataset.errorMessage : error.message, 'error');
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
      if (!signal.aborted) busy(false);
    }
  });
}

/* Native OS 2.0 lifecycle. Motion never gates content, checkout or image loading. */
(() => {
  const controllers = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 761px)');
  const fluidUrl = new URL('footer-fluid.js', document.currentScript.src).href;
  const ease = 'cubic-bezier(.22,1,.36,1)';
  let fluidRequested = false;
  function mount(root) {
    if (controllers.has(root)) return;
    const controller = new AbortController(), {signal} = controller;
    const cleanups = [], animations = new Set();
    const on = (el,event,fn,extra={}) => el?.addEventListener(event,fn,{...extra,signal});
    const find = selector => root.querySelector(selector);
    const all = selector => Array.from(root.querySelectorAll(selector));
    const surface = find('[data-home-section]');
    const motion = () => !reduced.matches && surface?.dataset.motion !== 'false';
    const animate = (el,frames,options={}) => {
      const animation = el.animate(frames,{duration:550,easing:ease,...options});
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation),() => animations.delete(animation));
      return animation;
    };
    controllers.set(root,() => {
      controller.abort(); animations.forEach(animation => animation.cancel());
      cleanups.forEach(fn => fn()); controllers.delete(root);
    });
    /* Language disclosure keeps native Shopify localization submission. */
    all('.moviwi-language-disclosure').forEach(disclosure => {
      const trigger = disclosure.querySelector('summary');
      const options = Array.from(disclosure.querySelectorAll('[name="language_code"]'));
      on(disclosure, 'toggle', () => {
        if (disclosure.open) {
          document.querySelectorAll('.moviwi-language-disclosure[open]').forEach(other => {
            if (other !== disclosure) other.open = false;
          });
        }
      });
      on(document, 'pointerdown', event => {
        if (!disclosure.contains(event.target)) disclosure.open = false;
      });
      on(document, 'focusin', event => {
        if (!disclosure.contains(event.target)) disclosure.open = false;
      });
      on(disclosure, 'keydown', event => {
        if (event.key === 'Escape' && disclosure.open) {
          event.preventDefault();
          event.stopPropagation();
          disclosure.open = false;
          trigger.focus({preventScroll:true});
          return;
        }
        const index = options.indexOf(event.target);
        const fromTrigger = event.target === trigger;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          disclosure.open = true;
          const next = fromTrigger ? (event.key === 'ArrowDown' ? 0 : options.length - 1) :
            (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options[next]?.focus({preventScroll:true});
        } else if (index >= 0 && (event.key === 'Home' || event.key === 'End')) {
          event.preventDefault();
          options[event.key === 'Home' ? 0 : options.length - 1]?.focus({preventScroll:true});
        }
      });
      cleanups.push(() => { disclosure.open = false; });
    });
    const header = find('[data-site-header]');
    if (header) {
      const mobile = find('.site-mobile-menu'), toggle = find('.site-menu-toggle');
      const wide=matchMedia('(min-width:1051px)');
      const setOpen=(open,restoreFocus=false)=>{
        mobile.hidden=!open;toggle.setAttribute('aria-expanded',String(open));
        toggle.setAttribute('aria-label',open?toggle.dataset.labelClose:toggle.dataset.labelOpen);
        if(restoreFocus)toggle.focus({preventScroll:true});
      };
      const update=()=>header.classList.toggle('is-scrolled',scrollY>8);
      on(window,'scroll',update,{passive:true});
      on(toggle,'click',()=>setOpen(mobile.hidden));
      on(header,'keydown',event=>{if(event.key==='Escape'){
        if(!mobile.hidden){event.preventDefault();setOpen(false,true);}
        all('details[open]').forEach(el=>el.open=false);
      }});
      on(document,'pointerdown',event=>{if(!header.contains(event.target))setOpen(false);});
      on(document,'focusin',event=>{if(!header.contains(event.target))setOpen(false);});
      all('.site-mobile-menu a').forEach(link=>on(link,'click',()=>setOpen(false,true)));
      on(wide,'change',()=>{if(wide.matches){const focused=mobile.contains(document.activeElement);setOpen(false);if(focused)find('.site-logo').focus({preventScroll:true});}});
      update();
    }
    all('[data-brand-button=primary]').forEach(button=>{
      on(button,'pointerenter',event=>{
        if(reduced.matches||button.closest('[data-button-motion=false]'))return;
        const rect=button.getBoundingClientRect();
        button.style.setProperty('--pointer-x',`${(event.clientX-rect.left)/rect.width*100}%`);
        button.style.setProperty('--pointer-y',`${(event.clientY-rect.top)/rect.height*100}%`);
      },{passive:true});
      on(button,'focus',()=>{button.style.removeProperty('--pointer-x');button.style.removeProperty('--pointer-y');});
    });
    if(motion()){
      const titles=all('h2').filter(title=>!title.closest('[data-game-story]'));
      const reveal=new IntersectionObserver(entries=>entries.forEach(entry=>{
        if(!entry.isIntersecting)return;
        entry.target.classList.add('motion-title-in');
        animate(entry.target,[{transform:'translateY(20px)',opacity:.65},{transform:'none',opacity:1}],{duration:650});reveal.unobserve(entry.target);
      }),{threshold:.25});
      titles.forEach(title=>reveal.observe(title));
      cleanups.push(()=>{reveal.disconnect();titles.forEach(title=>title.classList.remove('motion-title-in'));});
      if(surface?.dataset.edge==='curve'){
        let frame=0,visible=false;
        const draw=()=>{frame=0;if(!visible)return;const top=surface.getBoundingClientRect().top;const progress=Math.max(0,Math.min(1,1-top/innerHeight));surface.style.setProperty('--edge-radius',`${Math.min(72,innerWidth*.05)*(1-progress*.6)}px`);};
        const schedule=()=>{if(visible&&!frame)frame=requestAnimationFrame(draw);};
        const edgeObserver=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;schedule();});
        edgeObserver.observe(surface);on(window,'scroll',schedule,{passive:true});on(window,'resize',schedule,{passive:true});
        cleanups.push(()=>{edgeObserver.disconnect();cancelAnimationFrame(frame);surface.style.removeProperty('--edge-radius');});
      }
    }
    const hero=find('.hero-art');
    if(hero && surface?.dataset.pointerMotion==='true' && motion()){
      on(hero,'pointermove',event=>{
        if(event.pointerType!=='mouse')return;
        const rect=hero.getBoundingClientRect();
        hero.style.setProperty('--hero-x',`${(event.clientX-rect.left-rect.width/2)/65}px`);
        hero.style.setProperty('--hero-y',`${(event.clientY-rect.top-rect.height/2)/65}px`);
      },{passive:true});
      const reset=()=>{hero.style.removeProperty('--hero-x');hero.style.removeProperty('--hero-y');};
      on(hero,'pointerleave',reset);cleanups.push(reset);
    }
    const story=find('[data-product-story]'),stage=find('[data-product-stage]');
    if(story && stage && !story.hasAttribute('data-product-narrative') && motion()){
      const features=all('[data-product-features]>div');let frame=0,visible=false;
      const draw=()=>{
        frame=0;if(!visible||signal.aborted)return;
        const rect=story.getBoundingClientRect();
        const progress=Math.max(0,Math.min(1,(innerHeight*.55-rect.top)/Math.max(1,rect.height-innerHeight*.3)));
        stage.style.setProperty('--product-turn',`${desktop.matches?-12+progress*24:0}deg`);
        stage.style.setProperty('--product-roll',`${-3+progress*6}deg`);
        stage.style.setProperty('--product-scale',String(1+Math.sin(progress*Math.PI)*.08));
        stage.style.setProperty('--product-lift',`${-Math.sin(progress*Math.PI)*(desktop.matches?24:8)}px`);
        stage.style.setProperty('--orbit-turn',`${progress*45}deg`);
        let closest=0,distance=Infinity;
        features.forEach((feature,index)=>{const box=feature.getBoundingClientRect(),delta=Math.abs(box.top+box.height/2-innerHeight*.55);if(delta<distance){distance=delta;closest=index;}});
        features.forEach((feature,index)=>feature.classList.toggle('is-current',index===closest));
      };
      const schedule=()=>{if(visible&&!frame)frame=requestAnimationFrame(draw);};
      const observer=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;if(visible)schedule();},{rootMargin:'100px'});
      observer.observe(story);on(window,'scroll',schedule,{passive:true});on(window,'resize',schedule,{passive:true});
      const resetTilt=()=>{stage.style.setProperty('--tilt-x','0deg');stage.style.setProperty('--tilt-y','0deg');};
      if(surface.dataset.pointerMotion==='true'){
        on(stage,'pointermove',event=>{if(event.pointerType!=='mouse')return;const box=stage.getBoundingClientRect();stage.style.setProperty('--tilt-x',`${-(event.clientY-box.top-box.height/2)/70}deg`);stage.style.setProperty('--tilt-y',`${(event.clientX-box.left-box.width/2)/85}deg`);},{passive:true});on(stage,'pointerleave',resetTilt);
      }
      cleanups.push(()=>{observer.disconnect();cancelAnimationFrame(frame);stage.removeAttribute('style');features.forEach(el=>el.classList.remove('is-current'));});
    }
    const slides=all('[data-game-slide]'),tabs=all('[data-game-tab]'),games=find('[data-games]');
    if(slides.length && games){
      let selected=0,transitions=[];
      const settle=()=>{transitions.forEach(animation=>animation.cancel());transitions=[];slides.forEach((slide,index)=>{slide.hidden=index!==selected;slide.inert=index!==selected;slide.classList.remove('is-outgoing');});};
      const select=(index,immediate=false)=>{
        const next=(index+slides.length)%slides.length;if(next===selected&&!immediate)return;
        settle();const outgoing=slides[selected],incoming=slides[next],direction=next>selected?1:-1;
        selected=next;incoming.hidden=false;incoming.inert=false;
        tabs.forEach((tab,i)=>{tab.setAttribute('aria-selected',String(i===selected));tab.tabIndex=i===selected?0:-1;});
        if(surface)surface.style.backgroundColor=incoming.dataset.background;
        if(!immediate && motion()){
          outgoing.hidden=false;outgoing.inert=true;outgoing.classList.add('is-outgoing');
          const exit=animate(outgoing,[{transform:'none',opacity:1},{transform:`translateX(${-direction*9}%) rotate(${-direction*1.5}deg) scale(.96)`,opacity:0}],{duration:420});
          const enter=animate(incoming,[{transform:`translateX(${direction*11}%) rotate(${direction*1.5}deg) scale(.96)`,opacity:0},{transform:'none',opacity:1}],{duration:600});
          transitions=[exit,enter];enter.finished.then(()=>{if(slides[selected]===incoming)settle();},()=>{});
        }else settle();
      };
      const reserveHeight=()=>{
        settle();let height=0;
        slides.forEach(slide=>{
          const copy=slide.cloneNode(true);copy.removeAttribute('hidden');copy.classList.remove('is-outgoing');copy.inert=true;
          copy.style.cssText=`position:absolute;visibility:hidden;pointer-events:none;width:${games.clientWidth}px;height:auto;left:0;top:0`;
          copy.removeAttribute('id');copy.querySelectorAll('[id]').forEach(el=>el.removeAttribute('id'));
          games.append(copy);height=Math.max(height,copy.getBoundingClientRect().height);copy.remove();
        });
        const fixed=`${Math.ceil(height)}px`;games.style.minHeight=fixed;slides.forEach(slide=>slide.style.minHeight=fixed);
      };
      tabs.forEach((tab,index)=>{
        tab.setAttribute('aria-controls',`Game-${root.id}-${index}`);slides[index].id=`Game-${root.id}-${index}`;slides[index].setAttribute('role','tabpanel');
        on(tab,'click',()=>select(index));
        on(tab,'keydown',event=>{if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;event.preventDefault();select(event.key==='Home'?0:event.key==='End'?tabs.length-1:selected+(event.key==='ArrowRight'?1:-1));tabs[selected].focus();});
      });
      let startX=0,startY=0,width=0;
      on(games,'pointerdown',event=>{if(event.pointerType==='touch'){startX=event.clientX;startY=event.clientY;}},{passive:true});
      on(games,'pointerup',event=>{if(event.pointerType==='touch'&&Math.abs(event.clientX-startX)>60&&Math.abs(event.clientY-startY)<50)select(selected+(event.clientX<startX?1:-1));},{passive:true});
      const observer=new ResizeObserver(entries=>{const next=entries[0].contentRect.width;if(Math.abs(width-next)>.5){width=next;reserveHeight();}});
      observer.observe(games);cleanups.push(()=>{observer.disconnect();settle();});
      document.fonts?.ready.then(()=>{if(!signal.aborted)reserveHeight();});
      all('.game-visual img').forEach(image=>on(image,'load',reserveHeight,{once:true}));
      on(root,'shopify:block:select',event=>{const block=event.detail?.blockId;const index=slides.findIndex(slide=>slide.contains(event.target)||slide===event.target||(block&&slide.dataset.blockId===block));if(index>=0)select(index,true);});
      select(0,true);
    }
    // FLIP: expand the clicked card into a dialog, then return to the same card.
    const sources=new WeakMap(),dialogAnimations=new WeakMap();
    const geometry=(dialog,source)=>{
      const from=source.getBoundingClientRect(),to=dialog.getBoundingClientRect();
      return `translate(${from.left+from.width/2-to.left-to.width/2}px,${from.top+from.height/2-to.top-to.height/2}px) scale(${Math.max(.1,Math.min(1,from.width/to.width))},${Math.max(.1,Math.min(1,from.height/to.height))})`;
    };
    const closeDialog=dialog=>{
      if(!dialog.open||dialog.dataset.closing==='true')return;
      dialog.dataset.closing='true';dialogAnimations.get(dialog)?.cancel();dialog.querySelectorAll('video').forEach(video=>video.pause());
      const source=sources.get(dialog);
      const finish=()=>{delete dialog.dataset.closing;dialog.close();source?.classList.remove('motion-source-open');if(source?.isConnected)source.focus({preventScroll:true});};
      if(source?.isConnected&&motion()){
        const animation=animate(dialog,[{transform:'none',opacity:1},{transform:geometry(dialog,source),opacity:.3}],{duration:300});
        dialogAnimations.set(dialog,animation);animation.finished.then(finish,()=>{});
      }else finish();
    };
    all('[data-video-open],[data-media-open]').forEach(button=>on(button,'click',()=>{
      const selector=button.hasAttribute('data-video-open')?'[data-video-dialog]':'[data-image-dialog]';
      const dialog=button.closest('article')?.querySelector(selector)||find(selector);if(!dialog)return;
      const target=dialog.querySelector('[data-video-mount]');
      if(target&&!target.childElementCount)target.append(dialog.querySelector('template').content.cloneNode(true));
      dialogAnimations.get(dialog)?.cancel();delete dialog.dataset.closing;sources.set(dialog,button);dialog.showModal();
      if(motion()){
        const from=geometry(dialog,button);button.classList.add('motion-source-open');
        dialogAnimations.set(dialog,animate(dialog,[{transform:from,opacity:.45},{transform:'none',opacity:1}],{duration:550}));
      }
    }));
    all('[data-dialog-close]').forEach(button=>on(button,'click',()=>closeDialog(button.closest('dialog'))));
    all('dialog').forEach(dialog=>{
      on(dialog,'cancel',event=>{event.preventDefault();closeDialog(dialog);});
      on(dialog,'close',()=>{delete dialog.dataset.closing;sources.get(dialog)?.classList.remove('motion-source-open');dialog.querySelectorAll('video').forEach(video=>video.pause());});
      on(dialog,'click',event=>{if(event.target!==dialog)return;const box=dialog.getBoundingClientRect();if(event.clientX<box.left||event.clientX>box.right||event.clientY<box.top||event.clientY>box.bottom)closeDialog(dialog);});
      cleanups.push(()=>{dialogAnimations.get(dialog)?.cancel();sources.get(dialog)?.classList.remove('motion-source-open');dialog.querySelectorAll('video').forEach(video=>video.pause());if(dialog.open)dialog.close();});
    });
    mountNewsletter(find('.shopify-customer-form'), {signal, on, find});
    const success=find('[data-newsletter-success]');
    if(success){
      const show=()=>{if(signal.aborted)return;root.scrollIntoView({block:'center',behavior:'instant'});find('.newsletter-success')?.showModal();};
      show();
    }
    all('[data-copy-code]').forEach(button=>on(button,'click',async()=>{
      const input=button.parentElement.querySelector('input'),status=find('[data-copy-status]');
      try{await navigator.clipboard.writeText(input.value);button.textContent=button.dataset.copied;if(status)status.textContent=button.dataset.copied;}
      catch{input.focus();input.select();if(status)status.textContent=button.dataset.copyHelp;}
    }));
    const mist=find('[data-mw-footer-mist]');
    if(mist&&!reduced.matches){
      const lazy=new IntersectionObserver(entries=>{
        if(!entries.some(entry=>entry.isIntersecting))return;lazy.disconnect();
        if(window.MoviwiFooterFluid)window.MoviwiFooterFluid.mountAll();
        else if(!fluidRequested){fluidRequested=true;const script=document.createElement('script');script.src=fluidUrl;script.defer=true;script.addEventListener('error',()=>{fluidRequested=false;},{once:true});document.head.append(script);}
      },{rootMargin:'200px'});
      lazy.observe(mist);cleanups.push(()=>lazy.disconnect());
    }
  }
  const scan=()=>document.querySelectorAll('.shopify-section').forEach(mount);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',scan,{once:true});else scan();
  document.addEventListener('shopify:section:load',event=>mount(event.target));
  document.addEventListener('shopify:section:unload',event=>controllers.get(event.target)?.());
  document.addEventListener('shopify:section:select',event=>{const header=event.target.querySelector('[data-site-header]');if(header){header.classList.remove('compact');[...header.children].forEach(el=>el.inert=false);}});
  reduced.addEventListener('change',()=>document.querySelectorAll('.shopify-section').forEach(root=>{controllers.get(root)?.();mount(root);}));
})();
